import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TimelineConnector,
  timelineConnectorClasses,
  TimelineContent,
  TimelineDot,
  timelineDotClasses,
  TimelineDotProps,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { Alert, Stack, styled, Tooltip, Typography } from '@mui/material';
import classNames from 'classnames';

import { Revision } from '@api/editorialLayout/revisions';
import {
  Button,
  Checkbox,
  DataTree,
  DataTreeLanguage,
  IconButton,
  Loading,
} from '@components/base';
import { formatDate } from '@utils/dateUtils';

import CardWithExpandableArea from '../../CardWithExpandableArea';
import useParseRevision from '../hooks/useParseRevision';
import useRawDataRevision from '../hooks/useRawDataRevision';
import { RevisionId } from '../types';

import ParsingError from './ParsingError';

type RawDataRevisionContentProps = {
  revision: Revision;
  revisionId: RevisionId;
};

const classes = {
  compareCheck: 'compare-check',
  expanded: 'expanded',
  hasRepeatedMetadata: 'repeated-metadata',
  isHighlighted: 'is-highlighted',
  isMarkedForComparison: 'marked-for-comparison',
  isMultiple: 'is-multiple',
  isMuted: 'is-muted',
};

const highlightColor: TimelineDotProps['color'] = 'success';

const StyledTimelineItem = styled(TimelineItem)`
  max-width: 100%;
  min-height: 0;

  .${classes.isMuted}:not(.${classes.expanded}):not(:focus-within):not(:hover) {
    opacity: 0.5;
  }

  .${classes.compareCheck} {
    transition: ${({ theme }) => theme.transitions.duration.short}ms;
    opacity: 0;

    &:has(:focus-visible) {
      opacity: 1;
    }
  }

  &.${classes.expanded}, &.${classes.isMarkedForComparison}, &:hover,
  &:has(:focus-visible) {
    .${classes.compareCheck} {
      opacity: 1;
    }
  }

  .${classes.hasRepeatedMetadata} {
    min-width: 30px;
  }

  .${timelineConnectorClasses.root}.${classes.isHighlighted} {
    background: ${({ theme }) => theme.palette[highlightColor].main};
  }
`;

const StyledTimelineDot = styled(TimelineDot)(({ theme }) => ({
  alignSelf: 'center',
  borderWidth: 0,
  padding: 0,
  display: 'flex',

  [`&.${timelineDotClasses.filledGrey}`]: {
    color: theme.palette.getContrastText(theme.palette.grey[400]),
  },

  [`&.${classes.isHighlighted}`]: {
    color: theme.palette.getContrastText(theme.palette[highlightColor].main),
  },
}));

const StyledTimelineDateContent = styled(TimelineOppositeContent)`
  min-width: 156px;
  padding-left: 0;
  flex: 0;

  &.${classes.isMultiple} {
    margin-top: ${({ theme }) => theme.spacing()};
  }
`;

const StyledTimelineContent = styled(TimelineContent)`
  padding-right: 0;
`;

const StyledDotButton = styled(Button)`
  border-radius: 50%;
  height: 12px;
  min-width: 0;
  padding: 0;
  width: 12px;
`;

const RawMetadataRevision: FC<RawDataRevisionContentProps> = ({
  revision,
  revision: { hash, revision: revisionNumber, timestamp },
  revisionId,
}) => {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);

  const {
    queryResult: { isLoading, error, isError, data },
    parsedMetadata,
    parsingError,
  } = useParseRevision(revisionId, expanded);

  const {
    canAddRevisionToCompare,
    getRevisionGroup,
    hasRepeatedMetadata,
    highlightedHash,
    isMuted,
    revisions,
    revisionsToCompare,
    setHighlightedHash,
    toggleRevisionToCompare,
  } = useRawDataRevision();

  const isMarkedForComparison = revisionsToCompare.has(hash);

  const isExactMatch =
    isMarkedForComparison &&
    revisionsToCompare.get(hash)?.revisionNumber === revisionId.revisionNumber;

  const mute = isMuted(hash);
  const highlighted = highlightedHash === hash;
  const revisionGroup = getRevisionGroup(hash);

  if (!(revisions && revisionGroup)) return null;

  const isMultiple = revisionGroup && revisionGroup.length > 1;
  const isFirst = revisionGroup.at(-1)?.timestamp === timestamp;
  const timeUsed = revisionGroup.length - 1 - revisionGroup.indexOf(revision);
  const isLastInList = revisions.indexOf(revision) === revisions.length - 1;

  return (
    <StyledTimelineItem
      className={classNames({
        [classes.isMarkedForComparison]: isMarkedForComparison,
        [classes.expanded]: expanded,
      })}
    >
      <StyledTimelineDateContent
        className={classNames({ [classes.isMultiple]: isMultiple })}
        color='text.secondary'
        title={formatDate(timestamp, 'full')}
      >
        <Stack alignItems='flex-start'>
          <Typography variant='body2'>
            <code>{formatDate(timestamp, 'digital')}</code>
          </Typography>

          {revisions.length > 1 && (
            <Checkbox
              checked={isMarkedForComparison}
              className={classes.compareCheck}
              color={isExactMatch ? 'info' : 'default'}
              disabled={!isMarkedForComparison && !canAddRevisionToCompare}
              label={
                <Typography
                  color={isExactMatch ? 'info' : undefined}
                  component='span'
                  fontWeight='medium'
                  variant='caption'
                >
                  {t('compare')}
                </Typography>
              }
              labelPlacement='start'
              onChange={() => toggleRevisionToCompare(revisionId)}
              size='small'
            />
          )}
        </Stack>
      </StyledTimelineDateContent>

      {revisions && revisions.length > 1 && (
        <TimelineSeparator
          className={classNames({ [classes.hasRepeatedMetadata]: hasRepeatedMetadata })}
        >
          <StyledTimelineDot
            className={classNames({
              [classes.isMultiple]: isMultiple,
              [classes.isHighlighted]: highlighted,
            })}
            color={highlighted ? highlightColor : undefined}
          >
            <Tooltip title={highlighted ? t('clearHighlight') : 'Highlight uses'}>
              {isMultiple ? (
                <IconButton
                  color='inherit'
                  icon={highlighted ? 'check' : isFirst ? 'repeatOne' : 'repeat'}
                  onClick={() => setHighlightedHash(highlighted ? undefined : hash)}
                />
              ) : (
                <StyledDotButton
                  color='inherit'
                  onClick={() => setHighlightedHash(highlighted ? undefined : hash)}
                />
              )}
            </Tooltip>
          </StyledTimelineDot>

          {!isLastInList && (
            <TimelineConnector className={classNames({ [classes.isHighlighted]: highlighted })} />
          )}
        </TimelineSeparator>
      )}

      <StyledTimelineContent
        className={classNames({ [classes.expanded]: expanded, [classes.isMuted]: mute })}
        noWrap
      >
        <CardWithExpandableArea
          clickableArea='card'
          expandableContent={
            isLoading ? (
              <Loading BoxProps={{ p: 4 }} />
            ) : isError ? (
              <Alert severity='error'>{error.message}</Alert>
            ) : parsingError ? (
              <ParsingError>
                {data && <DataTree data={data} disableControls language='json' />}
              </ParsingError>
            ) : data && parsedMetadata ? (
              <DataTree data={parsedMetadata} language={data.type as DataTreeLanguage} />
            ) : null
          }
          expanded={expanded}
          heading={t('rawMetadata.revisionNo', { count: revisionNumber })}
          itemId={
            isMultiple
              ? t('rawMetadata.timeUsed', {
                  ordinalCount: t('number', { count: (timeUsed ?? 0) + 1, ordinal: true }),
                })
              : undefined
          }
          setExpanded={setExpanded}
        />
      </StyledTimelineContent>
    </StyledTimelineItem>
  );
};

export default RawMetadataRevision;
